import { type FC, Suspense } from 'react';
import classnames from 'classnames';
import { graphql, useFragment, useLazyLoadQuery } from 'react-relay';

import dibsCss from 'dibs-css';
import { VideoTile } from 'dibs-elements/exports/VideoTile';

import { useHomePageContext } from '../HomePageContext';

import styles from './HpSharedVideoComponentVideo.scss';

import { type HpSharedVideoComponentVideo_componentModule$key } from './__generated__/HpSharedVideoComponentVideo_componentModule.graphql';
import { type HpSharedVideoComponentVideoQuery as HpSharedVideoComponentVideoQueryType } from './__generated__/HpSharedVideoComponentVideoQuery.graphql';

type Props = {
    componentModule: HpSharedVideoComponentVideo_componentModule$key;
};

const componentModuleFragment = graphql`
    fragment HpSharedVideoComponentVideo_componentModule on VideoModule {
        videoId
        videoAutoplay
    }
`;

const videoQuery = graphql`
    query HpSharedVideoComponentVideoQuery($videoId: String!) {
        viewer {
            video(videoId: $videoId) {
                desktopFile: fileByVideoSize(size: w960h540) {
                    link
                }
                mobileFile: fileByVideoSize(size: w426h240) {
                    link
                }
            }
        }
    }
`;

type VideoContentProps = {
    videoId: string;
    autoPlay: boolean;
};

const VideoContent: FC<VideoContentProps> = ({ videoId, autoPlay }) => {
    const { isMobile } = useHomePageContext();

    const data = useLazyLoadQuery<HpSharedVideoComponentVideoQueryType>(videoQuery, {
        videoId,
    });
    const videoUrl = isMobile
        ? data?.viewer?.video?.mobileFile?.link
        : data?.viewer?.video?.desktopFile?.link;

    if (!videoUrl) {
        return null;
    }

    return (
        <div className={classnames(dibsCss.relative, styles.videoContainer)}>
            <VideoTile controls sourceUrl={videoUrl} noSpinner loop={false} autoPlay={autoPlay} />
        </div>
    );
};

const HpSharedVideoComponentVideo: FC<Props> = ({ componentModule: componentModuleRef }) => {
    const componentModule = useFragment(componentModuleFragment, componentModuleRef);
    const { videoId, videoAutoplay } = componentModule || {};

    if (!videoId) {
        return null;
    }

    return (
        <Suspense fallback={null}>
            <VideoContent videoId={videoId} autoPlay={!!videoAutoplay} />
        </Suspense>
    );
};

export default HpSharedVideoComponentVideo;
